import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { usePrevious } from 'react-use';
import { erc20Abi, type Abi, type Address, maxUint256 } from 'viem';
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi';

import { USDT_ADDRESSES } from '@/constants/addresses';

import { maxApprovalSelector, useApprovalSettingsStore } from '@/store/approvalSettings';

import { trackError } from '@/utils/errors';
import { isEthereumAddress, type EthereumAddress } from '@/utils/models';
import { TokenInfo } from '@/utils/token.types';

import { useAllowance, allowanceQueryKey } from './useAllowance';

interface UseApproveArgs {
  /** The address of the contract that will be approved to tranfer the token */
  spender?: Address;
  /** The token that will be approved */
  token?: TokenInfo;
  /** Exact amount required for the swap. Used when the user has opted out of max approvals. */
  requiredAmount?: bigint;
}

/**
 * Hook to approve a token to be spent by a contract
 */
export function useApprove({ spender, token, requiredAmount }: UseApproveArgs) {
  const queryClient = useQueryClient();
  const tokenAddress =
    token?.address && isEthereumAddress(token.address)
      ? (token.address as EthereumAddress)
      : undefined;
  const { data: allowance, refetch: refetchAllowance } = useAllowance(
    tokenAddress,
    spender,
    token?.chainId,
  );

  const needsAllowanceReset = getNeedsUsdtAllowanceReset({ token, allowance });
  const maxApproval = useApprovalSettingsStore(maxApprovalSelector);

  const {
    data: hash,
    writeContract,
    error: approvalError,
    status: writeContractStatus,
    reset,
  } = useWriteContract();

  const prevTokenAddress = usePrevious(token?.address);
  const prevChainId = usePrevious(token?.chainId);

  const { data: approvalReceipt, isLoading: isApproving } = useWaitForTransactionReceipt({ hash });

  const approvedAmount = getApprovalAmount({
    needsAllowanceReset,
    maxApproval,
    requiredAmount,
  });

  const approve = () => {
    if (!spender || !token || !isEthereumAddress(token.address)) return;

    if (approvedAmount === undefined) {
      // User opted out of max approvals but no exact amount is available
      // (e.g. token decimals haven't hydrated or the input failed to parse).
      // Block the approval rather than silently falling back to unlimited.
      trackError(new Error('useApprove: missing required amount for exact approval'), {
        tokenAddress: token.address,
        chainId: token.chainId,
        spender,
      });
      return;
    }

    writeContract({
      address: token.address,
      functionName: 'approve',
      abi: getApproveABI(token.address, token.chainId),
      args: [spender, approvedAmount],
    });
  };

  useEffect(() => {
    // When the token address and chain Id changes, the writeContract state needs to be reset,
    // or it will continue to return the previous hash.
    if (token?.address !== prevTokenAddress && token?.chainId !== prevChainId) reset();
  }, [prevChainId, prevTokenAddress, reset, token?.address, token?.chainId]);

  useEffect(() => {
    if (approvalReceipt?.status === 'success' && approvedAmount !== undefined) {
      // Optimistically write the approved amount into the allowance cache so
      // dependent UI updates immediately, then verify against on-chain state
      // after a short delay in case the node hasn't propagated the receipt.
      queryClient.setQueryData(
        allowanceQueryKey(tokenAddress, token?.chainId, spender),
        approvedAmount,
      );

      const timeoutId = setTimeout(() => {
        refetchAllowance();
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [
    approvalReceipt,
    refetchAllowance,
    approvedAmount,
    queryClient,
    tokenAddress,
    token?.chainId,
    spender,
  ]);

  return {
    approve,
    allowance,
    needsAllowanceReset,
    isApproving,
    approvalError,
    approvalReceipt,
    refetchAllowance,
    isPendingApproval: writeContractStatus === 'pending',
  };
}

/**
 * Picks the amount to pass to ERC20 approve.
 * - USDT reset path always wins (returns 0n).
 * - When max approval is on, returns maxUint256.
 * - When max approval is off, returns the exact required amount, or undefined
 *   if no amount is available. Callers must treat undefined as "can't approve
 *   right now" rather than silently falling back to max.
 */
export function getApprovalAmount({
  needsAllowanceReset,
  maxApproval,
  requiredAmount,
}: {
  needsAllowanceReset: boolean;
  maxApproval: boolean;
  requiredAmount: bigint | undefined;
}): bigint | undefined {
  if (needsAllowanceReset) return 0n;
  if (maxApproval) return maxUint256;
  return requiredAmount;
}

/**
 * USDT's non-standard approve reverts when changing a non-zero allowance to a
 * different non-zero value. Reset to 0 first when the existing allowance is non-zero.
 */
export function getNeedsUsdtAllowanceReset({
  token,
  allowance,
}: {
  token: TokenInfo | undefined;
  allowance: bigint | undefined;
}): boolean {
  if (!token) return false;
  if (token.address !== USDT_ADDRESSES[token.chainId]) return false;
  if (allowance === undefined) return false;
  return allowance > 0n;
}

export function getApproveABI(tokenAddress: string, chainId: number): Abi {
  // USDT is a special case because it doesn't follow the ERC20 standard
  if (tokenAddress === USDT_ADDRESSES[chainId]) {
    return [
      {
        constant: false,
        inputs: [
          { name: '_spender', type: 'address' },
          { name: '_value', type: 'uint256' },
        ],
        name: 'approve',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
      },
    ];
  }

  return erc20Abi;
}
